import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlusIcon, TrashIcon, PencilSimpleIcon, CoinsIcon, GlobeIcon } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const MODES = [
  { v: "cash", l: "Cash", icon: CoinsIcon },
  { v: "online", l: "Online", icon: GlobeIcon },
];

export default function Expenses() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);

  const load = useCallback(async () => {
    try {
      const [exp, cats] = await Promise.all([
        api.get(`/expenses?date=${date}`),
        api.get("/expense-categories"),
      ]);
      setItems(exp.data);
      setCategories((cats.data || []).map((c) => c.name));
    } catch { toast.error("Failed"); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const totals = MODES.reduce((acc, m) => {
    acc[m.v] = items.filter((x) => x.mode === m.v).reduce((s, x) => s + x.amount, 0);
    return acc;
  }, {});
  const grand = Object.values(totals).reduce((a, b) => a + b, 0);

  const money = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-10" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="overline text-slate-500">Money out</div>
          <h1 className="font-display text-4xl font-black">Expenses</h1>
          <p className="text-slate-500 mt-2">Cash and online expenditures — categorised and totaled daily.</p>
        </div>
        <div className="flex gap-3 items-end">
          <div>
            <Label className="overline">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none w-40" data-testid="expense-date-filter" />
          </div>
          <AddDialog date={date} categories={categories} onDone={load} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 border border-slate-200 bg-white">
        {MODES.map((m) => (
          <div key={m.v} className="p-6 border-r border-slate-200 last:border-r-0" data-testid={`exp-total-${m.v}`}>
            <div className="flex items-center justify-between">
              <span className="overline text-slate-500">{m.l}</span>
              <m.icon size={16} className="text-slate-400" />
            </div>
            <div className="font-mono-num text-2xl font-bold mt-3">{money(totals[m.v])}</div>
          </div>
        ))}
        <div className="p-6 bg-slate-900 text-white" data-testid="exp-total-all">
          <div className="overline text-white/60">Total</div>
          <div className="font-mono-num text-2xl font-bold mt-3">{money(grand)}</div>
        </div>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 border-b border-slate-200">
              <TableHead className="uppercase text-xs tracking-wider">Mode</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Category</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Note</TableHead>
              <TableHead className="text-right uppercase text-xs tracking-wider">Amount</TableHead>
              {isOwner && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} className="hover:bg-slate-50 border-b border-slate-100" data-testid={`expense-row-${it.id}`}>
                <TableCell className="font-semibold uppercase text-xs tracking-wider">{it.mode}</TableCell>
                <TableCell className="text-slate-700">{it.category || "—"}</TableCell>
                <TableCell className="text-slate-600">{it.note || "—"}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold text-[#FF2400]">{money(it.amount)}</TableCell>
                {isOwner && (
                  <TableCell>
                    <div className="inline-flex gap-2">
                      <EditDialog item={it} categories={categories} onDone={load} />
                      <button
                        onClick={async () => {
                          if (!confirm("Delete this expense?")) return;
                          try { await api.delete(`/expenses/${it.id}`); toast.success("Deleted"); load(); }
                          catch { toast.error("Failed"); }
                        }}
                        className="text-slate-400 hover:text-[#FF2400]"
                        data-testid={`delete-expense-${it.id}`}
                      >
                        <TrashIcon size={16} />
                      </button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10">No expenses yet for this date.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {!isOwner && (
        <p className="text-xs text-slate-500" data-testid="staff-notice">
          Note · you can add new expenses but only the owner can edit or delete existing ones.
        </p>
      )}
    </div>
  );
}

function EditDialog({ item, categories, onDone }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(item.mode);
  const [amount, setAmount] = useState(String(item.amount || ""));
  const [category, setCategory] = useState(item.category || "Other");
  const [note, setNote] = useState(item.note || "");
  const [d, setD] = useState(item.date);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/expenses/${item.id}`, {
        mode, amount: parseFloat(amount), category, note, date: d,
      });
      toast.success("Updated"); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-slate-400 hover:text-brand-blue" data-testid={`edit-expense-${item.id}`} title="Edit (owner only)">
          <PencilSimpleIcon size={16} />
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300">
        <DialogHeader><DialogTitle className="font-display text-brand-blue">Edit expense</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(categories && categories.length ? categories : ["Other"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Amount (₹)</Label>
              <Input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required className="rounded-none font-mono-num" />
            </div>
            <div>
              <Label className="overline">Date</Label>
              <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="rounded-none" />
            </div>
          </div>
          <div>
            <Label className="overline">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" />
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-brand-blue hover:bg-brand-blue-ink text-white">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddDialog({ date, categories, onDone }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("cash");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("Other");
  const [note, setNote] = useState("");
  const [d, setD] = useState(date);

  useEffect(() => { setD(date); }, [date]);
  useEffect(() => {
    // If the selected category isn't in the list anymore, fall back to the first
    if (categories && categories.length && !categories.includes(category)) {
      setCategory(categories.includes("Other") ? "Other" : categories[0]);
    }
  }, [categories, category]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", { mode, amount: parseFloat(amount), category, note, date: d });
      toast.success("Expense recorded");
      setOpen(false); setAmount(""); setCategory("Other"); setNote("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none bg-slate-900 hover:bg-black text-white" data-testid="add-expense-button">
          <PlusIcon size={16} className="mr-2" /> Add
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300">
        <DialogHeader><DialogTitle className="font-display">Log expense</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-expense-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="rounded-none" data-testid="expense-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="rounded-none" data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(categories && categories.length ? categories : ["Other"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Amount (₹)</Label>
              <Input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required className="rounded-none font-mono-num" data-testid="expense-amount-input" />
            </div>
            <div>
              <Label className="overline">Date</Label>
              <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="rounded-none" data-testid="expense-date-input" />
            </div>
          </div>
          <div>
            <Label className="overline">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" data-testid="expense-note-input" />
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-slate-900 hover:bg-black" data-testid="expense-submit-button">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
